<template>
  <div class="sms-sim">
    <div class="sms-sim__header d-flex align-center px-4 pt-4">
      <h2 class="sim-toolbar-title">SMS Simulator</h2>
      <v-spacer />
      <v-btn
        color="primary"
        rounded
        elevation="0"
        :loading="dispatching"
        :disabled="dispatching"
        @click="onDispatchPrompt"
      >
        <v-icon left>mdi-send</v-icon>
        Trigger today's prompt now
      </v-btn>
    </div>

    <simulator-dev-banner
      :delivery-route="dbState && dbState.deliveryRoute"
      :phone="dbState && dbState.phone"
    />

    <v-container fluid class="pa-4">
      <v-row>
        <v-col cols="12" md="6">
          <simulator-phone-pane
            :messages="messageBubbles"
            :contact-name="contactName"
            :loading="loading"
          />
        </v-col>
        <v-col cols="12" md="6">
          <simulator-composer
            :sending="sending"
            class="mb-4"
            @send="onSend"
          />
          <simulator-db-panel :db-state="dbState" />
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
/**
 * SMS Simulator — Phase 0 lite-channel demo surface.
 *
 * Verification recipe (web side, mirrors plan §2.13):
 *   1. Open /participants/:id/sms-simulator for an SMS participant.
 *   2. Click "Trigger today's prompt" — a PROMPT bubble appears.
 *   3. Type Y / N / STOP / HELP — DB panel updates within ~2s (polling).
 *
 * Polling cadence: 2000ms. SSE is not used; no backend SSE endpoint exists.
 * Route registration is gated by isNonProd() in router/config.js.
 */
import { mapState, mapGetters, mapActions } from 'vuex'
import SimulatorDevBanner from '@/components/sms-sim/SimulatorDevBanner.vue'
import SimulatorPhonePane from '@/components/sms-sim/SimulatorPhonePane.vue'
import SimulatorComposer from '@/components/sms-sim/SimulatorComposer.vue'
import SimulatorDbPanel from '@/components/sms-sim/SimulatorDbPanel.vue'

export default {
  name: 'SmsSimulator',
  components: {
    SimulatorDevBanner,
    SimulatorPhonePane,
    SimulatorComposer,
    SimulatorDbPanel,
  },
  computed: {
    ...mapState('smsSimStates', [
      'outbox',
      'dbState',
      'loading',
      'sending',
      'dispatching',
    ]),
    ...mapGetters('smsSimStates', ['messageBubbles']),
    participantId() {
      const raw = this.$route.params.id
      const parsed = parseInt(raw, 10)
      return Number.isFinite(parsed) ? parsed : null
    },
    contactName() {
      const first = (this.outbox || []).find(r => r.fromLabel || r.from_label)
      return (first && (first.fromLabel || first.from_label)) || 'Tenant'
    },
  },
  mounted() {
    if (this.participantId) {
      this.startPolling({
        participantId: this.participantId,
        intervalMs: 2000,
      })
    }
  },
  watch: {
    participantId(newId, oldId) {
      if (newId === oldId) return
      this.stopPolling()
      if (newId) {
        this.startPolling({
          participantId: newId,
          intervalMs: 2000,
        })
      }
    },
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    ...mapActions('smsSimStates', [
      'startPolling',
      'stopPolling',
      'sendInbound',
      'dispatchPrompt',
    ]),
    onSend(body) {
      if (!this.participantId) return
      this.sendInbound({ participantId: this.participantId, body })
    },
    onDispatchPrompt() {
      if (!this.participantId) return
      this.dispatchPrompt({ participantId: this.participantId })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.sms-sim {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.sim-toolbar-title {
  color: $bh-blue;
  font-weight: 600;
}
</style>
